import { useAuth0 } from '@auth0/auth0-react';
import { useEffect, type ReactNode } from 'react';
import { AuthError, type AuthErrorBrand } from './AuthError';

interface Props {
  children: ReactNode;
  /** Optional override for the loading view. Defaults to a centred spinner. */
  fallback?: ReactNode;
  /** App identity shown on the access-denied / error screen. */
  brand?: AuthErrorBrand;
}

/**
 * Gates a subtree behind Auth0 authentication. While loading shows a fallback;
 * if unauthenticated, kicks off `loginWithRedirect` and remembers where to
 * return to via `appState.returnTo`.
 *
 * If the redirect came back with an error instead of a session (e.g.
 * `access_denied` — the user isn't subscribed to this app), we DON'T retry the
 * login (that loops forever) — we render {@link AuthError} so the user can log
 * out and sign in with an account that has access.
 */
export function RequireAuth({ children, fallback, brand }: Props) {
  const { isAuthenticated, isLoading, error, loginWithRedirect } = useAuth0();

  useEffect(() => {
    if (!isLoading && !isAuthenticated && !error) {
      void loginWithRedirect({
        appState: { returnTo: window.location.pathname + window.location.search },
      });
    }
  }, [isAuthenticated, isLoading, error, loginWithRedirect]);

  if (error && !isAuthenticated) {
    return <AuthError error={error} brand={brand} />;
  }

  if (isLoading || !isAuthenticated) {
    return (
      <>
        {fallback ?? (
          <div className="flex min-h-screen items-center justify-center bg-slate-50 text-slate-500">
            <div className="flex items-center gap-3">
              <div className="size-3 animate-pulse rounded-full bg-[var(--color-primary)]" />
              <span>Signing in…</span>
            </div>
          </div>
        )}
      </>
    );
  }

  return <>{children}</>;
}
